import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomSelectBox, CsbOptionProps, SELECT_BEHAVIOUR } from '@flos/react-components';
import track, { errorTypes } from 'c9-tracking';
import { ROUTE_NAMES } from '../../router/RouterApp';
import { useGlobalState, useGlobalDispatch } from '../../common/context/AppContext';
import { useCustomerMarking } from '../../common/customHooks/useCustomerMarking';
import { useStakeholder } from '../../common/customHooks/useStakeholder';
import { IAppConfig } from '../../common/interface/IAppConfig';
import { getCsbOptions } from './helpers';
import { useInsuranceStatus } from '../../common/customHooks/useInsuranceStatus';
import { NonDomesticAddressOverlay, CustomerMarkingOverlay, BornholmOverlay } from '../shared/CustomerOverlay.view';
import { IStakeHolder } from '../../common/interface/IPayload';

type AppConfigProps = {
  appConfig: IAppConfig;
};

export const HouseholdForm = ({ appConfig }: AppConfigProps): JSX.Element => {
  const content = appConfig.resourceBundles['onsa.travel.household'].read();
  const navigate = useNavigate();
  const { customerHasMarking, isValidInsurance, householdSize, cprNo } = useGlobalState();
  const appDispatch = useGlobalDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [buttonOptions, setButtonOptions] = useState<CsbOptionProps[]>(getCsbOptions(content));
  const { customerMarking } = useCustomerMarking(appConfig, customerHasMarking);
  const { isBornholm, stakeholder } = useStakeholder<IStakeHolder>(appConfig);
  const [isInvalidAddress, setIsInvalidAddress] = useState(false);
  const { userCanProceed } = useInsuranceStatus(appConfig, isValidInsurance);
  const isLoggedOn = window.topContext.user.isLoggedOn;

  useEffect(() => {
    if (householdSize) {
      const newState = buttonOptions;
      newState[+householdSize - 1].checked = true;
      setButtonOptions(newState);
    }

    if (customerMarking !== undefined) {
      appDispatch({ customerHasMarking: customerMarking });
      setIsOpen(customerMarking);
    }
    if (userCanProceed.canProceed !== undefined) {
      appDispatch({ isValidInsurance: userCanProceed.canProceed });
      setIsInvalidAddress(
        !userCanProceed.canProceed && userCanProceed.message === 'Customer not having a domestic address'
      );
      if (!userCanProceed.canProceed) {
        track.fault(`Customer can't proceed due to ${userCanProceed.message}`, errorTypes.rule, 'Login');
      }
    }
  }, [buttonOptions, householdSize, customerMarking, appDispatch, userCanProceed, isBornholm]);

  const onSelectHandler = (_value: string, index: number) => {
    const newState = buttonOptions;
    newState[index].checked = true;
    setButtonOptions(newState);
    next();
  };

  const next = () => {
    const index = buttonOptions.findIndex((e) => e.checked);
    appDispatch({
      householdSize: (index + 1).toString(),
      cprNo: {
        ...cprNo,
        value: stakeholder?.cprNo!,
        valid: true
      },
      stateChange: true
    });
    track.interact([
      { key: 'action', value: 'Form submitted' },
      { key: 'household', value: buttonOptions[index].label as string }
    ]);

    let goto = ROUTE_NAMES.CUSTOMER_USER;
    if (isLoggedOn /* && !customerHasMarking && isValidInsurance && !isBornholm*/) {
      goto = ROUTE_NAMES.PACKAGES;
    }

    navigate(goto);
  };

  return (
    <>
      <form noValidate={true} name={`onsa-travel-form-house-hold-size`} id={`onsa-travel-form-house-hold-size`}>
        <CustomSelectBox
          behaviour={SELECT_BEHAVIOUR.RADIO}
          name={'house-hold-size'}
          options={buttonOptions}
          onChange={onSelectHandler}
        />
      </form>

      <CustomerMarkingOverlay isOpen={isOpen} setIsOpen={setIsOpen} appConfig={appConfig} />
      <NonDomesticAddressOverlay
        isInvalidAddress={isInvalidAddress}
        setIsInvalidAddress={setIsInvalidAddress}
        appConfig={appConfig}
      />
      <BornholmOverlay isOpen={!!isBornholm} setIsOpen={() => null} appConfig={appConfig} />
    </>
  );
};
